import { useEffect, useState, useCallback, useRef } from 'react';
import { MessageCircle, Send, RefreshCw, Search, Check, CheckCheck, AlertCircle, UserPlus, X, Reply, Trash2, Phone, Clock } from 'lucide-react';
import { crmApi, Funil } from '../crmApi';
import { UserSession } from '../../../types';

interface Props {
  session?: UserSession | null;
  initialNumero?: string;
  onNumeroConsumed?: () => void;
}

const fmtHora = (d: any) => {
  if (!d) return '';
  try {
    const dt = new Date(d.$date || d);
    return dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const fmtData = (d: any) => {
  if (!d) return '';
  try {
    const dt = new Date(d.$date || d);
    const hoje = new Date();
    const ontem = new Date(); ontem.setDate(ontem.getDate() - 1);
    if (dt.toDateString() === hoje.toDateString()) return fmtHora(d);
    if (dt.toDateString() === ontem.toDateString()) return 'Ontem';
    return dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch { return ''; }
};

const tempoRelativo = (d: any) => {
  if (!d) return '';
  try {
    const dt = new Date(d.$date || d).getTime();
    const diff = Date.now() - dt;
    if (diff < 0) return 'agora';
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'agora';
    if (min < 60) return `há ${min}min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `há ${h}h`;
    const dias = Math.floor(h / 24);
    if (dias < 7) return `há ${dias}d`;
    const sem = Math.floor(dias / 7);
    if (sem < 4) return `há ${sem}sem`;
    const meses = Math.floor(dias / 30);
    return `há ${meses}mê${meses > 1 ? 's' : 's'}`;
  } catch { return ''; }
};

const formatarNumero = (n: string) => {
  const d = (n || '').replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('55')) return `+55 ${d.slice(2, 4)} ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12 && d.startsWith('55')) return `+55 ${d.slice(2, 4)} ${d.slice(4, 8)}-${d.slice(8)}`;
  return `+${d}`;
};

const iniciais = (nome: string | undefined, numero: string) => {
  const n = (nome || '').trim();
  if (n) {
    const partes = n.split(/\s+/).filter(Boolean);
    const a = partes[0]?.[0] || '';
    const b = partes.length > 1 ? partes[partes.length - 1][0] : (partes[0]?.[1] || '');
    return (a + b).toUpperCase() || '??';
  }
  const d = (numero || '').replace(/\D/g, '');
  return d.slice(-2) || '??';
};

const InboxWhatsapp = ({ session, initialNumero, onNumeroConsumed }: Props) => {
  const [conversas, setConversas] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [naoLidas, setNaoLidas] = useState(false);
  const [ativa, setAtiva] = useState<string | null>(null);
  const [mensagens, setMensagens] = useState<any[]>([]);
  const [resposta, setResposta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [mostrarConverter, setMostrarConverter] = useState(false);
  const [convertendo, setConvertendo] = useState(false);
  const [formLead, setFormLead] = useState({ nome: '', empresa: '', email: '', observacoes: '', temperatura: 'morno', funilId: '', etapaId: '', valor: '' });
  const [funis, setFunis] = useState<Funil[]>([]);
  const [sucessoConversao, setSucessoConversao] = useState<string | null>(null);
  const [respondendoMsg, setRespondendoMsg] = useState<any | null>(null);
  const [mostrarLigacao, setMostrarLigacao] = useState(false);
  const [registrandoLigacao, setRegistrandoLigacao] = useState(false);
  const [formLigacao, setFormLigacao] = useState({ status: 'feita', duracao: '', observacoes: '' });
  const fimRef = useRef<HTMLDivElement | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const list = await crmApi.whatsapp.inbox(session, naoLidas);
      setConversas(list);
    } catch (e: any) { console.error(e); }
    finally { setCarregando(false); }
  }, [session, naoLidas]);

  // Auto-open conversation when initialNumero is provided (e.g. from CarteiraGix)
  useEffect(() => {
    if (!initialNumero) return;
    carregar().then(() => carregarConversa(initialNumero));
    if (onNumeroConsumed) onNumeroConsumed();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialNumero]);

  const carregarConversa = async (numero: string) => {
    setAtiva(numero);
    setErro(null);
    try {
      const msgs = await crmApi.whatsapp.conversa(session, numero);
      setMensagens(msgs);
      setTimeout(() => fimRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e: any) { setErro(e.message); }
  };

  const enviar = async () => {
    if (!ativa || !resposta.trim()) return;
    setEnviando(true);
    setErro(null);
    try {
      const r = await crmApi.whatsapp.enviar(session, {
        numero: ativa,
        mensagem: resposta,
        quotedMessageId: respondendoMsg?.messageId || undefined,
      });
      if (r.success) {
        setResposta('');
        setRespondendoMsg(null);
        await carregarConversa(ativa);
        carregar();
      } else {
        setErro(r.error || 'Falha ao enviar mensagem');
      }
    } catch (e: any) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const converterEmLead = async () => {
    if (!ativa || !formLead.nome.trim()) return;
    setConvertendo(true);
    setErro(null);
    try {
      const r = await crmApi.whatsapp.converterEmLead(session, {
        numero: ativa,
        nome: formLead.nome.trim(),
        empresa: formLead.empresa,
        email: formLead.email,
        observacoes: formLead.observacoes,
        temperatura: formLead.temperatura,
        funilId: formLead.funilId || undefined,
        etapaId: formLead.etapaId || undefined,
        valor: formLead.valor ? (parseInt(formLead.valor, 10) || 0) / 100 : undefined,
      });
      if (r.success) {
        setMostrarConverter(false);
        setFormLead({ nome: '', empresa: '', email: '', observacoes: '', temperatura: 'morno', funilId: '', etapaId: '', valor: '' });
        const msg = r.jaExistia
          ? `Lead já existia: ${r.lead?.nome}. Conversa vinculada.`
          : r.negocioId
            ? `Lead "${r.lead?.nome}" criado e adicionado ao funil!`
            : `Lead "${r.lead?.nome}" criado e conversa vinculada!`;
        setSucessoConversao(msg);
        setTimeout(() => setSucessoConversao(null), 5000);
      } else {
        setErro(r.error || 'Falha ao criar lead');
      }
    } catch (e: any) {
      setErro(e.message);
    } finally {
      setConvertendo(false);
    }
  };

  const apagarConversa = async () => {
    if (!ativa) return;
    if (!window.confirm('Apagar essa conversa?\nEsta ação não pode ser desfeita e removerá todas as mensagens.')) return;
    try {
      const r = await crmApi.whatsapp.apagarConversa(session, ativa);
      if (r.success) {
        setAtiva(null);
        setMensagens([]);
        await carregar();
      } else {
        setErro(r.error || 'Falha ao apagar conversa');
      }
    } catch (e: any) {
      setErro(e.message);
    }
  };

  const registrarLigacao = async () => {
    if (!ativa) return;
    setRegistrandoLigacao(true);
    setErro(null);
    try {
      const v = conversaAtiva?.vinculos || {};
      const vinculo: any = {};
      if (v.leadId) vinculo.leadId = v.leadId;
      if (v.contatoId) vinculo.contatoId = v.contatoId;
      if (v.empresaContaId) vinculo.empresaContaId = v.empresaContaId;
      if (v.negocioId) vinculo.negocioId = v.negocioId;
      if (Object.keys(vinculo).length === 0) {
        setErro('Conversa sem vínculo. Converta em Lead primeiro para registrar ligações.');
        setRegistrandoLigacao(false);
        return;
      }
      const dur = parseInt(formLigacao.duracao || '0', 10) || 0;
      const statusLabel: Record<string, string> = {
        feita: 'Ligação feita',
        recebida: 'Ligação recebida',
        perdida: 'Ligação perdida',
        'nao-atendida': 'Ligação não atendida',
      };
      const descricao = `${statusLabel[formLigacao.status] || 'Ligação'}${dur > 0 ? ` (${Math.floor(dur/60)}m${dur%60}s)` : ''}${formLigacao.observacoes ? ` — ${formLigacao.observacoes}` : ''}`;
      await crmApi.atividades.criar(session, {
        tipo: 'ligacao',
        descricao,
        ...vinculo,
        metadata: {
          statusLigacao: formLigacao.status,
          duracaoSegundos: dur,
          numero: ativa,
        },
      });
      setMostrarLigacao(false);
      setFormLigacao({ status: 'feita', duracao: '', observacoes: '' });
    } catch (e: any) {
      setErro(e.message || 'Falha ao registrar ligação');
    } finally {
      setRegistrandoLigacao(false);
    }
  };

  useEffect(() => { carregar(); }, [carregar]);

  // Escuta evento global para abrir uma conversa específica vinda de outras telas (ex: Pipeline)
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const numero = (detail.numero || '').toString().replace(/\D/g, '');
      if (!numero) return;
      // Garante que a lista de conversas esteja atualizada e abre a conversa
      try { await carregar(); } catch {}
      // Normalização básica BR (força 9º dígito quando vier 12 dígitos com 55)
      let canon = numero;
      if (canon.length <= 11 && !canon.startsWith('55')) canon = '55' + canon;
      if (canon.length === 12 && canon.startsWith('55') && ['6','7','8','9'].includes(canon[4])) {
        canon = canon.slice(0, 4) + '9' + canon.slice(4);
      }
      carregarConversa(canon);
    };
    window.addEventListener('crm-open-inbox', handler);
    return () => window.removeEventListener('crm-open-inbox', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregar]);
  useEffect(() => {
    crmApi.listarFunis(session).then(setFunis).catch(() => {});
  }, [session]);

  useEffect(() => {
    const t = setInterval(carregar, 15000);
    return () => clearInterval(t);
  }, [carregar]);

  useEffect(() => {
    if (!ativa) return;
    const t = setInterval(async () => {
      try {
        const msgs = await crmApi.whatsapp.conversa(session, ativa);
        setMensagens(msgs);
      } catch {}
    }, 8000);
    return () => clearInterval(t);
  }, [ativa, session]);

  const filtradas = conversas.filter(c => {
    const q = busca.trim().toLowerCase();
    return !q || (c.numero || '').includes(q) || (c.ultimaMensagem || '').toLowerCase().includes(q);
  });

  const conversaAtiva = conversas.find(c => c.numero === ativa);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-0 h-[calc(100vh-200px)] bg-[#f0f2f5] rounded-xl overflow-hidden border border-gray-200">
      {/* Sidebar */}
      <aside className="bg-white flex flex-col border-r border-gray-200 min-h-0">
        <div className="bg-[#f0f2f5] px-4 py-3 flex items-center justify-between border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-800">Conversas</h3>
          <button onClick={carregar} title="Atualizar"
            className="p-1.5 hover:bg-white rounded-full text-gray-600">
            <RefreshCw className={`h-4 w-4 ${carregando ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="p-2 bg-white border-b border-gray-100">
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-1.5">
            <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <input value={busca} onChange={e => setBusca(e.target.value)}
              placeholder="Pesquisar conversa"
              className="flex-1 text-sm outline-none bg-transparent" />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600 mt-2 px-1 cursor-pointer">
            <input type="checkbox" checked={naoLidas} onChange={e => setNaoLidas(e.target.checked)} />
            Apenas não lidas
          </label>
        </div>

        <div className="flex-1 overflow-y-auto bg-white">
          {carregando && conversas.length === 0 ? (
            <p className="p-6 text-xs text-gray-400 text-center">Carregando…</p>
          ) : filtradas.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="h-10 w-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">Nenhuma conversa</p>
              <p className="text-xs text-gray-400 mt-1">Conversas chegam aqui quando alguém te manda mensagem.</p>
            </div>
          ) : filtradas.map(c => {
            const ativo = ativa === c.numero;
            return (
              <button key={c.numero} onClick={() => carregarConversa(c.numero)}
                className={`w-full text-left px-3 py-3 border-b border-gray-50 flex items-center gap-3 transition-colors ${
                  ativo ? 'bg-[#f0f2f5]' : 'hover:bg-gray-50'
                }`}>
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                  {iniciais(c.nomeContato, c.numero)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-gray-900 truncate">{c.nomeContato || formatarNumero(c.numero)}</span>
                    <span className="text-[10px] text-gray-500 flex-shrink-0">{fmtData(c.ultimaData)}</span>
                  </div>
                  {c.nomeContato && (
                    <p className="text-[10px] text-gray-400 truncate">{formatarNumero(c.numero)}</p>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                      {c.ultimaDirecao === 'out' && <CheckCheck className="h-3 w-3 text-blue-500 flex-shrink-0" />}
                      {c.ultimaMensagem}
                    </p>
                    {c.naoLidas > 0 && (
                      <span className="text-[10px] bg-green-500 text-white px-1.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center font-semibold flex-shrink-0">
                        {c.naoLidas}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className="text-[10px] text-gray-400">{tempoRelativo(c.ultimaData)}</p>
                    {c.ultimaDirecao === 'in' && (
                      <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-px rounded-full font-semibold flex items-center gap-0.5"
                        title="Cliente está aguardando sua resposta">
                        <Clock className="h-2.5 w-2.5" /> aguardando
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Painel da conversa */}
      <main className="flex flex-col min-h-0 relative" style={{ backgroundColor: '#efeae2' }}>
        {!ativa ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
            <MessageCircle className="h-16 w-16 opacity-30 mb-3" />
            <p className="text-base font-medium">Selecione uma conversa</p>
            <p className="text-xs mt-1">Estilo WhatsApp Web</p>
          </div>
        ) : (
          <>
            <div className="bg-[#f0f2f5] px-4 py-2.5 flex items-center gap-3 border-b border-gray-200 z-10">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                {iniciais(conversaAtiva?.nomeContato, ativa)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{conversaAtiva?.nomeContato || formatarNumero(ativa)}</p>
                <p className="text-[11px] text-gray-500 truncate">
                  {conversaAtiva?.nomeContato ? formatarNumero(ativa) : (conversaAtiva?.naoLidas > 0 ? `${conversaAtiva.naoLidas} não lida(s)` : 'WhatsApp')}
                </p>
              </div>
              <button
                onClick={() => { setFormLigacao({ status: 'feita', duracao: '', observacoes: '' }); setMostrarLigacao(true); }}
                className="p-1.5 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-md flex-shrink-0"
                title="Registrar ligação telefônica">
                <Phone className="h-4 w-4" />
              </button>
              <button
                onClick={() => { setFormLead(f => ({ ...f, nome: f.nome || conversaAtiva?.nomeContato || formatarNumero(ativa) })); setMostrarConverter(true); }}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-md hover:bg-blue-700 inline-flex items-center gap-1.5 flex-shrink-0"
                title="Converter esta conversa em Lead do CRM">
                <UserPlus className="h-3.5 w-3.5" /> Converter em Lead
              </button>
              <button
                onClick={apagarConversa}
                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md flex-shrink-0"
                title="Apagar conversa">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 md:px-12 py-4 space-y-1">
              {mensagens.length === 0 && (
                <p className="text-xs text-gray-500 text-center py-8 bg-white/60 rounded-lg max-w-xs mx-auto">Nenhuma mensagem ainda.</p>
              )}
              {mensagens.map((m, idx) => {
                const out = m.direcao === 'out';
                const prevOut = idx > 0 ? mensagens[idx - 1].direcao === 'out' : null;
                const mostraTail = prevOut !== out;
                return (
                  <div key={m.id || idx} className={`group flex ${out ? 'justify-end' : 'justify-start'}`}>
                    <div className={`relative max-w-[75%] md:max-w-[60%] px-2.5 py-1.5 shadow-sm ${
                      out
                        ? 'bg-[#d9fdd3] text-gray-900 rounded-l-lg rounded-tr-lg ' + (mostraTail ? 'rounded-br-none' : 'rounded-br-lg')
                        : 'bg-white text-gray-900 rounded-r-lg rounded-tl-lg ' + (mostraTail ? 'rounded-bl-none' : 'rounded-bl-lg')
                    }`}>
                      {m.quotedText && (
                        <div className="mb-1 px-2 py-1 bg-black/5 border-l-2 border-blue-500 rounded text-[11px] text-gray-700 truncate">
                          {m.quotedText}
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap break-words pr-14">{m.mensagem}</p>
                      <span className="text-[10px] text-gray-500 absolute bottom-1 right-2 flex items-center gap-0.5">
                        {fmtHora(m.data)}
                        {out && (m.status === 'falha'
                          ? <AlertCircle className="h-3 w-3 text-red-500" />
                          : m.status === 'lida'
                            ? <CheckCheck className="h-3 w-3 text-blue-500" />
                            : m.status === 'entregue'
                              ? <CheckCheck className="h-3 w-3 text-gray-400" />
                              : <Check className="h-3 w-3 text-gray-400" />
                        )}
                      </span>
                      {!out && m.messageId && (
                        <button
                          onClick={() => setRespondendoMsg(m)}
                          title="Responder"
                          className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-2 -right-2 bg-white border border-gray-200 rounded-full p-1 shadow hover:bg-blue-50">
                          <Reply className="h-3 w-3 text-blue-600" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={fimRef} />
            </div>

            {erro && (
              <div className="px-4 py-2 bg-red-50 border-t border-red-200 text-xs text-red-700 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span className="flex-1">{erro}</span>
                <button onClick={() => setErro(null)} className="text-red-500 hover:text-red-700 font-bold">×</button>
              </div>
            )}

            <div className="bg-[#f0f2f5] px-3 py-2 flex flex-col gap-2 border-t border-gray-200">
              {respondendoMsg && (
                <div className="flex items-start gap-2 bg-white border-l-4 border-blue-500 px-2 py-1.5 rounded">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-blue-600">Respondendo</p>
                    <p className="text-xs text-gray-600 truncate">{respondendoMsg.mensagem}</p>
                  </div>
                  <button onClick={() => setRespondendoMsg(null)}
                    className="text-gray-400 hover:text-gray-700 flex-shrink-0">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              <div className="flex items-end gap-2">
              <textarea value={resposta}
                onChange={e => setResposta(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    enviar();
                  }
                }}
                rows={1}
                placeholder="Digite uma mensagem"
                className="flex-1 px-4 py-2.5 text-sm bg-white rounded-lg outline-none resize-none max-h-32"
                style={{ minHeight: '42px' }} />
              <button onClick={enviar} disabled={enviando || !resposta.trim()}
                className="w-10 h-10 bg-green-600 text-white rounded-full hover:bg-green-700 disabled:opacity-40 flex items-center justify-center flex-shrink-0">
                <Send className="h-4 w-4" />
              </button>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Toast de sucesso */}
      {sucessoConversao && (
        <div className="fixed bottom-6 right-6 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg text-sm flex items-center gap-2 z-50">
          <UserPlus className="h-4 w-4" />
          {sucessoConversao}
        </div>
      )}

      {/* Modal Converter em Lead */}
      {mostrarConverter && ativa && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !convertendo && setMostrarConverter(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-blue-600" /> Converter em Lead
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">Telefone: {formatarNumero(ativa)}</p>
              </div>
              <button onClick={() => setMostrarConverter(false)} disabled={convertendo}
                className="p-1 hover:bg-gray-100 rounded text-gray-500">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nome do contato *</label>
                <input value={formLead.nome} onChange={e => setFormLead({ ...formLead, nome: e.target.value })}
                  placeholder="Ex: João Silva" autoFocus
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Empresa</label>
                <input value={formLead.empresa} onChange={e => setFormLead({ ...formLead, empresa: e.target.value })}
                  placeholder="Empresa do contato (opcional)"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">E-mail</label>
                <input type="email" value={formLead.email} onChange={e => setFormLead({ ...formLead, email: e.target.value })}
                  placeholder="email@exemplo.com (opcional)"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Temperatura</label>
                <div className="flex gap-2">
                  {[
                    { v: 'frio', l: '🧊 Frio', c: 'bg-blue-50 border-blue-300 text-blue-700' },
                    { v: 'morno', l: '🌡️ Morno', c: 'bg-orange-50 border-orange-300 text-orange-700' },
                    { v: 'quente', l: '🔥 Quente', c: 'bg-red-50 border-red-300 text-red-700' },
                  ].map(t => (
                    <button key={t.v} type="button"
                      onClick={() => setFormLead({ ...formLead, temperatura: t.v })}
                      className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md border ${
                        formLead.temperatura === t.v ? t.c : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}>
                      {t.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Observações</label>
                <textarea value={formLead.observacoes} onChange={e => setFormLead({ ...formLead, observacoes: e.target.value })}
                  rows={2} placeholder="Notas sobre o contato..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-blue-500 outline-none resize-none" />
              </div>
              {funis.length > 0 && (
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                    🎯 Adicionar ao Funil de Vendas <span className="text-gray-400 font-normal">(opcional)</span>
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <select value={formLead.funilId}
                      onChange={e => setFormLead({ ...formLead, funilId: e.target.value, etapaId: '' })}
                      className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:border-blue-500 outline-none bg-white">
                      <option value="">— Não adicionar —</option>
                      {funis.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                    </select>
                    <select value={formLead.etapaId} disabled={!formLead.funilId}
                      onChange={e => setFormLead({ ...formLead, etapaId: e.target.value })}
                      className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:border-blue-500 outline-none bg-white disabled:opacity-40">
                      <option value="">Etapa…</option>
                      {(funis.find(f => f.id === formLead.funilId)?.etapas || []).map(e => (
                        <option key={e.id} value={e.id}>{e.nome}</option>
                      ))}
                    </select>
                  </div>
                  {formLead.funilId && (
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-700 mb-1">Valor da oportunidade (R$)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={(() => {
                          const cents = parseInt(formLead.valor || '0', 10) || 0;
                          return (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        })()}
                        onChange={e => {
                          const onlyDigits = e.target.value.replace(/\D/g, '');
                          setFormLead({ ...formLead, valor: onlyDigits });
                        }}
                        placeholder="0,00"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:border-blue-500 outline-none" />
                    </div>
                  )}
                </div>
              )}
              <p className="text-[11px] text-gray-500 bg-gray-50 px-3 py-2 rounded">
                ℹ️ Todo o histórico desta conversa será automaticamente vinculado ao lead.
              </p>
            </div>
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setMostrarConverter(false)} disabled={convertendo}
                className="px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-md">
                Cancelar
              </button>
              <button onClick={converterEmLead} disabled={convertendo || !formLead.nome.trim()}
                className="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-1.5">
                <UserPlus className="h-3.5 w-3.5" />
                {convertendo ? 'Criando...' : 'Criar Lead'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Registrar Ligação */}
      {mostrarLigacao && ativa && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !registrandoLigacao && setMostrarLigacao(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-emerald-600" /> Registrar ligação
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">{conversaAtiva?.nomeContato || formatarNumero(ativa)}</p>
              </div>
              <button onClick={() => setMostrarLigacao(false)} disabled={registrandoLigacao}
                className="p-1 hover:bg-gray-100 rounded text-gray-500">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Tipo</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: 'feita', l: '📞 Eu liguei', c: 'bg-emerald-50 border-emerald-300 text-emerald-700' },
                    { v: 'recebida', l: '📲 Cliente ligou', c: 'bg-blue-50 border-blue-300 text-blue-700' },
                    { v: 'nao-atendida', l: '🔕 Não atendeu', c: 'bg-orange-50 border-orange-300 text-orange-700' },
                    { v: 'perdida', l: '❌ Perdi a chamada', c: 'bg-red-50 border-red-300 text-red-700' },
                  ].map(t => (
                    <button key={t.v} type="button"
                      onClick={() => setFormLigacao({ ...formLigacao, status: t.v })}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md border ${
                        formLigacao.status === t.v ? t.c : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}>
                      {t.l}
                    </button>
                  ))}
                </div>
              </div>
              {(formLigacao.status === 'feita' || formLigacao.status === 'recebida') && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Duração (segundos)</label>
                  <input type="number" min="0" value={formLigacao.duracao}
                    onChange={e => setFormLigacao({ ...formLigacao, duracao: e.target.value })}
                    placeholder="Ex: 180"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-emerald-500 outline-none" />
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Observações</label>
                <textarea value={formLigacao.observacoes}
                  onChange={e => setFormLigacao({ ...formLigacao, observacoes: e.target.value })}
                  rows={3} placeholder="O que foi conversado…"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-emerald-500 outline-none resize-none" />
              </div>
            </div>
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setMostrarLigacao(false)} disabled={registrandoLigacao}
                className="px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-md">
                Cancelar
              </button>
              <button onClick={registrarLigacao} disabled={registrandoLigacao}
                className="px-4 py-1.5 bg-emerald-600 text-white text-sm font-semibold rounded-md hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                {registrandoLigacao ? 'Registrando...' : 'Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InboxWhatsapp;
